import { InputAdornment, TextField } from '@mui/material';
import { IRON } from 'styles/Colors';
import { noop } from 'lodash';
import { SET_DATA } from 'helpers/Constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  validPasswordCase,
  validPasswordEmail,
  validPasswordLength,
  validPasswordNumber,
  validPasswordPrevious,
  validPasswordsMatch,
  validPasswordSpecialChars
} from 'reg_redesign/helper';
import PasswordRequirements from 'components/component_lib/PasswordRequirements';
import React, { useEffect, useState } from 'react';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

export const validationText = [
  {
    rule: validPasswordLength,
    displayValue: 'Must be at least 12 characters'
  },
  {
    rule: validPasswordNumber,
    displayValue: 'Must contain at least one number'
  },
  {
    rule: validPasswordSpecialChars,
    displayValue: 'Must contain at least one special character (@$!%*#?& )'
  },
  {
    rule: validPasswordCase,
    displayValue: 'Mix of both upper and lower case letters'
  },
  {
    rule: validPasswordEmail,
    displayValue: 'Cannot contain your email address'
  }
];

const passwordsMatchVerification = {
  rule: validPasswordsMatch,
  displayValue: 'Passwords match'
};

export default function PasswordWithConfirmation({
  additionalProp,
  onChange = noop,
  onMount = noop,
  value,
  useConfirmation = false,
  checkPreviousPasswords = false
}) {
  const dispatch = useDispatch();
  const password = useSelector((state) => state.password);
  const passwordResetToken = useSelector((state) => state.passwordResetToken);
  const passwordConfirmation = useSelector(
    (state) => state.passwordConfirmation
  );
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [previousPassword, setPreviousPassword] = useState({
    text: 'Cannot be your previously used password',
    status: 'unTouch'
  });

  const validationRequirements = useConfirmation
    ? validationText.concat([passwordsMatchVerification])
    : validationText;

  useEffect(() => {
    onMount(value, validationRequirements, false);
  }, []);

  const setResults = (status) => {
    setPreviousPassword({
      ...previousPassword,
      status
    });
    dispatch({
      type: SET_DATA,
      payload: {
        additionalProps: {
          password: {
            previousPassword: status
          }
        }
      }
    });
  };

  const callCheckPreviousPasswords = (pass, passConfirmation) => {
    if (checkPreviousPasswords) {
      if (pass && pass === passConfirmation) {
        validPasswordPrevious(pass, passConfirmation, passwordResetToken).then(
          (result) => {
            setResults(result ? 'pass' : 'fail');
          }
        );
      } else {
        setResults('unTouch');
      }
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val, validationRequirements, false);
    callCheckPreviousPasswords(val, passwordConfirmation);
  };

  const handleConfirmationChange = (e) => {
    const val = e.target.value;

    dispatch({
      type: SET_DATA,
      payload: {
        passwordConfirmation: val
      }
    });
    onChange(password, validationRequirements, false);
    callCheckPreviousPasswords(password, val);
  };

  const validations = checkPreviousPasswords
    ? additionalProp?.validationsDisplay.concat([previousPassword])
    : additionalProp?.validationsDisplay;

  return (
    <div>
      <TextField
        id='password'
        label='New Password'
        type={passwordHidden ? 'password' : 'text'}
        variant='outlined'
        value={value}
        InputLabelProps={{ style: { color: IRON } }}
        FormHelperTextProps={{ style: { margin: '0', fontSize: '12px' } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              {passwordHidden ? (
                <VisibilityIcon onClick={() => setPasswordHidden(false)} />
              ) : (
                <VisibilityOffIcon onClick={() => setPasswordHidden(true)} />
              )}
            </InputAdornment>
          )
        }}
        onChange={handleChange}
        fullWidth
      />
      {useConfirmation && (
        <TextField
          id='password_confirmation'
          label='Confirm New Password'
          type={passwordHidden ? 'password' : 'text'}
          variant='outlined'
          value={passwordConfirmation}
          css={{ marginTop: '1rem' }}
          InputLabelProps={{ style: { color: IRON } }}
          FormHelperTextProps={{ style: { margin: '0', fontSize: '12px' } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                {passwordHidden ? (
                  <VisibilityIcon onClick={() => setPasswordHidden(false)} />
                ) : (
                  <VisibilityOffIcon onClick={() => setPasswordHidden(true)} />
                )}
              </InputAdornment>
            )
          }}
          onChange={handleConfirmationChange}
          fullWidth
        />
      )}

      {!additionalProp?.hideRequirements && (
        <PasswordRequirements rules={validations} />
      )}
    </div>
  );
}
